import { CommonModule } from '@angular/common'
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import {
  NotificationCampaignService,
  NotificationChannel,
  OccasionTemplate,
  OccasionType,
  SaveTemplatePayload
} from '../../../../services/notification-campaign.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { CKEditorModule } from '@ckeditor/ckeditor5-angular'
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading,
  Font,
  Link,
  List,
  Alignment,
  BlockQuote,
  Indent,
  RemoveFormat
} from 'ckeditor5'

import type { EditorConfig } from 'ckeditor5'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { loadBusinesses } from '../../../../store/business/business.actions'
import { Store } from '@ngrx/store'
import { NgSelectModule } from '@ng-select/ng-select'
import { Business } from '../../../../store/business/business.state'
import { selectAllBusinesses } from '../../../../store/business/business.selectors'
import { Observable } from 'rxjs'

@Component({
  selector: 'app-notification-occasion-template',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CapitalizePipe,
    ReactiveFormsModule,
    CKEditorModule,
    NgSelectModule
  ],
  templateUrl: './notification-campaign-occasion-template.component.html',
  styleUrls: ['./notification-campaign-occasion-template.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NotificationOccasionTemplateComponent implements OnInit {
  private store = inject(Store)
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  templates: OccasionTemplate[] = []
  loading = false

  showModal = false
  submitting = false
  error = ''
  testRunMessage = ''
  readonly PUSH_TITLE_MAX_LENGTH = 65
  readonly PUSH_MESSAGE_MAX_LENGTH = 240
  readonly occasionOptions: { value: OccasionType; label: string }[] = [
    { value: 'birthday', label: 'Birthday' },
    { value: 'work_anniversary', label: 'Work Anniversary' }
  ]
  readonly availablePlaceholders = [
    { key: '{firstName}', label: 'First Name' },
    { key: '{lastName}', label: 'Last Name' },
    { key: '{years}', label: 'Years of Service' },
    { key: '{businessName}', label: 'Business Name' }
  ]
  readonly timezones = [
    'UTC',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Singapore',
    'Europe/London',
    'America/New_York',
    'America/Los_Angeles'
  ]

  // ✅ Cron-schedule jaisa UI — frequency select karke expression generate karo
  form: {
    occasion: OccasionType
    title: string
    message: string
    channel: NotificationChannel
    // scheduleType: 'daily' | 'custom'
    // runTime: string
    // cronExpression: string
    // timezone: string,
    businessId?: string
  } = this.emptyForm()

  // =========================
  // CKEDITOR
  // =========================

  public Editor = ClassicEditor

  public editorConfig: EditorConfig = {
    licenseKey: 'GPL',

    plugins: [
      Essentials,
      Paragraph,
      Heading,
      Bold,
      Italic,
      Underline,
      Strikethrough,
      Font,
      Link,
      List,
      Alignment,
      BlockQuote,
      Indent,
      RemoveFormat
    ],

    toolbar: [
      'undo',
      'redo',
      '|',
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'link',
      'blockQuote',
      '|',
      'removeFormat'
    ],

    alignment: {
      options: ['left', 'center', 'right', 'justify'] as any
    },

    root: {
      placeholder: 'Write your message...'
    }
  }

  private editorInstance: ClassicEditor | null = null

  constructor(
    private campaignService: NotificationCampaignService,
    private sanitizer: DomSanitizer,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadTemplates()
    this.loadAllBusinesses()
  }

  loadTemplates(): void {
    this.loading = true
    this.campaignService.listTemplates().subscribe({
      next: res => {
        this.templates = res
        this.loading = false
      },
      error: () => (this.loading = false)
    })
  }

  loadAllBusinesses(): void {
    this.store.dispatch(
      loadBusinesses({ params: new URLSearchParams({ disabled: 'false' }).toString() })
    )
  }

  customBusinessSearchFn = (term: string, item: any) =>
    `${item.businessName}`.toLowerCase().includes(term.toLowerCase())

  private emptyForm() {
    return {
      occasion: 'birthday' as OccasionType,
      title: '',
      message: '',
      channel: 'push' as NotificationChannel,
      // scheduleType: 'daily' as 'daily' | 'custom',
      // runTime: '02:00',
      // cronExpression: '',
      // timezone: 'UTC',
      businessId: ''
    }
  }

  openModal(): void {
    this.form = this.emptyForm()
    this.error = ''
    this.showModal = true
  }

  closeModal(): void {
    this.showModal = false
    this.error = ''
  }

  // private buildCronExpression(): string {
  //   if (this.form.scheduleType === 'custom') {
  //     if (!this.form.cronExpression.trim()) throw new Error('Please enter a cron expression.')
  //     return this.form.cronExpression.trim()
  //   }
  //   const [hour, minute] = this.form.runTime.split(':').map(Number)
  //   return `${minute} ${hour} * * *`
  // }

  save(): void {
    this.error = ''

    if (!this.form.occasion) {
      this.error = 'Please select an occasion type.'
      return
    }
    if (!this.form.title.trim()) {
      this.error = 'Please enter a title.'
      return
    }
    // if (!this.form.message.trim()) {
    //   this.error = 'Please enter a message.'
    //   return
    // }
    const plainMessage = this.form.message
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, '')
      .trim()
    if (!plainMessage) {
      this.error = 'Please enter a message.'
      return
    }

    if (this.isPushSelected) {
      if (this.form.title.length > this.PUSH_TITLE_MAX_LENGTH) {
        this.error = `Title exceeds ${this.PUSH_TITLE_MAX_LENGTH} characters, required for push notifications. Current: ${this.form.title.length}`
        return
      }
      const plainLength = this.getPlainTextLength(this.form.message)
      if (plainLength > this.PUSH_MESSAGE_MAX_LENGTH) {
        this.error = `Message exceeds ${this.PUSH_MESSAGE_MAX_LENGTH} characters, required for push notifications. Current: ${plainLength}`
        return
      }
    }

    // let cronExpression: string
    // try {
    //   cronExpression = this.buildCronExpression()
    // } catch (e: any) {
    //   this.error = e.message
    //   return
    // }

    const payload: SaveTemplatePayload = {
      occasion: this.form.occasion as OccasionType,
      title: this.form.title.trim(),
      message: this.form.message.trim(),
      channel: this.form.channel,
      // cronExpression,
      // timezone: this.form.timezone
      businessId: this.form.businessId || undefined
    }

    this.submitting = true
    this.campaignService.createDefaultTemplate(payload).subscribe({
      next: () => {
        this.submitting = false
        this.showModal = false
        this.loadTemplates()
      },
      error: (err: any) => {
        this.submitting = false
        this.error = err?.error?.message || err?.message || 'Failed to save template'
      }
    })
  }

  deactivate(t: OccasionTemplate): void {
    this.campaignService.deactivateTemplate(t.id).subscribe(() => this.loadTemplates())
  }

  testRun(t: OccasionTemplate): void {
    this.testRunMessage = ''
    this.campaignService.testRunTemplate(t.id).subscribe({
      next: () =>
        (this.testRunMessage = `Triggered "${t.title}" — check campaign history in a moment.`),
      error: (err: any) => (this.testRunMessage = err?.error?.message || 'Failed to trigger')
    })
  }

  occasionLabel(o: OccasionType): string {
    return this.occasionOptions.find(x => x.value === o)?.label || o
  }

  channelLabel(c: NotificationChannel): string {
    return { push: 'Push', email: 'Email', both: 'Push + Email' }[c]
  }

  scopeLabel(t: OccasionTemplate): string {
    return t.businessId ? t.businessName || 'Business override' : 'Default (All Businesses)'
  }

  insertPlaceholder(placeholder: string): void {
    if (!this.editorInstance) return
    const editor = this.editorInstance
    editor.model.change((writer: any) => {
      const insertPosition = editor.model.document.selection.getFirstPosition()
      writer.insertText(placeholder, insertPosition)
    })
    editor.editing.view.focus()
  }

  private getPlainTextLength(html: string): number {
    const div = document.createElement('div')
    div.innerHTML = html
    return (div.textContent || div.innerText || '').trim().length
  }

  get isPushSelected(): boolean {
    return this.form.channel === 'push' || this.form.channel === 'both'
  }

  get titleCharCount(): number {
    return this.form.title.length
  }

  get messageCharCount(): number {
    return this.getPlainTextLength(this.form.message)
  }

  get isTitleOverLimit(): boolean {
    return this.isPushSelected && this.titleCharCount > this.PUSH_TITLE_MAX_LENGTH
  }

  get isMessageOverLimit(): boolean {
    return this.isPushSelected && this.messageCharCount > this.PUSH_MESSAGE_MAX_LENGTH
  }

  // ✅ Live preview — sample data ke saath
  get titlePreview(): string {
    return this.form.title
      .replace(/{firstName}/g, 'John')
      .replace(/{lastName}/g, 'Doe')
      .replace(/{years}/g, '3')
      .replace(/{businessName}/g, 'Acme Corp')
  }

  get messagePreview(): SafeHtml {
    const resolved = this.form.message
      .replace(/{firstName}/g, 'John')
      .replace(/{lastName}/g, 'Doe')
      .replace(/{years}/g, '3')
      .replace(/{businessName}/g, 'Acme Corp')
    return this.sanitizer.bypassSecurityTrustHtml(resolved)
  }

  // ✅ Channel checkbox logic — campaign-modal jaisa
  isChannelSelected(channel: 'push' | 'email'): boolean {
    if (this.form.channel === 'both') return true
    return this.form.channel === channel
  }

  onChannelChange(channel: 'push' | 'email' | 'both', event: Event): void {
    const checked = (event.target as HTMLInputElement).checked

    if (channel === 'both') {
      this.form.channel = checked ? 'both' : 'both'
      return
    }

    if (checked) {
      if (channel === 'push') {
        this.form.channel =
          this.form.channel === 'email' || this.form.channel === 'both' ? 'both' : 'push'
      } else {
        this.form.channel =
          this.form.channel === 'push' || this.form.channel === 'both' ? 'both' : 'email'
      }
      return
    }

    if (channel === 'push') {
      this.form.channel = this.form.channel === 'both' ? 'email' : 'push'
    } else {
      this.form.channel = this.form.channel === 'both' ? 'push' : 'email'
    }
  }

  onEditorReady(editor: any): void {
    this.editorInstance = editor
  }
}
